import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  ClockIcon,
  LocationMarkerIcon,
} from '@heroicons/react/outline';
import { toIntlDateTimeFormat } from '@zedoc/date';
import Tag from '../../../common/components/Tag';
import Card from '../../../components/Card';
import Table from '../../../components/lists/Table';
import FormFieldText from '../../../components/forms/FormFieldText';
import FormFieldSearch from '../../../components/forms/FormFieldSearch';
import {
  getProcedureStatusType,
  CLINICIAN_OPTION_ALL,
  PROCEDURE_STATUS_OPTION_ALL,
  getProcedureStatusOptions,
  getProcedureStatusLabel,
} from '../helpers';
import CalendarTimeView from '../../../components/CalendarTimeView';
import Layout from '../components/Layout';
import { ProcedureDetails } from '../../../components/dialogs/EpisodeOfCareDetails';
import useProcedures from '../hooks/useProcedures';
import useClinicians from '../hooks/useClinicians';

export const getProcedureColumns = ({ language, t }) => [
  {
    key: 'projectName',
    title: t('forms:projectName.label'),
    render: (_, { context: { projectLabel, projectId } }) => {
      const hasError = !projectLabel && !projectId;

      return (
        <span className={`${hasError ? 'text-danger-500' : ''}`}>
          {projectLabel || projectId || 'None assigned'}
        </span>
      );
    },
  },
  {
    key: 'recipientInformation',
    title: t('forms:recipientInformation.label'),
    render: (_, { patientId, context: { patientName } }) => {
      const hasPatientNameError = !patientName;
      const hasPatientIdError = !patientId;

      return (
        <div className="stack-0">
          <span className={`${hasPatientNameError ? 'text-danger-500' : ''}`}>
            {patientName || patientId || 'None assigned'}
          </span>
          <span
            className={`text-xs ${
              hasPatientIdError ? 'text-danger-500' : 'text-secondary'
            }`}
          >
            ID: {patientId || 'None assigned'}
          </span>
        </div>
      );
    },
  },
  {
    key: 'clinicianName',
    title: t('forms:clinician.label'),
    render: (_, { context: { clinicianName } }) => clinicianName,
  },
  {
    key: 'date',
    title: t('date'),
    render: (_, { start }) => {
      return (
        <div className="cluster-2 items-center">
          <CalendarIcon className="w-4 h-4" />
          <span>{toIntlDateTimeFormat(language, start)}</span>
        </div>
      );
    },
  },
  {
    key: 'time',
    title: t('time'),
    render: (_, { start, end }) => {
      return (
        <div className="cluster-2 items-center">
          <ClockIcon className="w-4 h-4" />
          <span>
            {toIntlDateTimeFormat(language, start, {
              timeStyle: 'short',
            })}
            {' - '}
            {toIntlDateTimeFormat(language, end, {
              timeStyle: 'short',
            })}
          </span>
        </div>
      );
    },
  },
  {
    key: 'duration',
    title: t('duration'),
    render: (_, { start, end }) => <span>{(end - start) / 60000} min</span>,
  },
  {
    key: 'location',
    title: t('location'),
    render: (_, { context: { location } }) => {
      return (
        <div className="cluster-2 items-center">
          <LocationMarkerIcon className="w-4 h-4 flex-shrink-0" />
          <span>{location}</span>
        </div>
      );
    },
  },
  {
    key: 'procedureStatus',
    title: t('forms:status.label'),
    render: (_, { status }) => {
      return (
        <Tag size="small" type={getProcedureStatusType(status)} block>
          {getProcedureStatusLabel({ status, t })}
        </Tag>
      );
    },
  },
  {
    key: 'description',
    title: t('forms:description.label'),
    render: (_, { context: { description } }) => description,
  },
  {
    key: 'specialInstructions',
    title: t('forms:specialInstructions.label'),
    render: (_, { context: { notes } }) => notes,
  },
  {
    key: 'eventId',
    title: t('forms:procedureId.label'),
  },
];

const isToday = (date) => {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const Procedures = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [currentProcedure, setCurrentProcedure] = useState();
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [currentStatus, setCurrentStatus] = useState(
    PROCEDURE_STATUS_OPTION_ALL,
  );
  const [patientName, setPatientName] = useState('');
  const [patientNameInput, setPatientNameInput] = useState('');
  const [clinicianId, setClinicianId] = useState(null);
  const [mode, setMode] = useState('table');

  // Calendar date range state
  const [calendarDateRange, setCalendarDateRange] = useState(null);

  // Fetch clinicians list for the dropdown
  const { clinicians, loading: cliniciansLoading } = useClinicians();

  const statusFilter =
    currentStatus !== PROCEDURE_STATUS_OPTION_ALL ? currentStatus : undefined;

  // Table data fetch (paginated, with all filters)
  const {
    procedures: fetchedProcedures,
    total,
    loading,
    error,
  } = useProcedures({
    page,
    pageSize,
    status: statusFilter,
    patientName: patientName || undefined,
    clinicianId: clinicianId || undefined,
  });

  // Calendar data fetch (date-filtered, with all filters, no pagination)
  const isCalendarMode = mode === 'calendar';
  const { procedures: calendarProcedures, loading: calendarLoading } =
    useProcedures(
      isCalendarMode
        ? {
            page: 1,
            pageSize: 500,
            status: statusFilter,
            patientName: patientName || undefined,
            clinicianId: clinicianId || undefined,
            startDate: calendarDateRange?.startDate,
            endDate: calendarDateRange?.endDate,
            view: 'calendar',
          }
        : { skip: true },
    );

  const handleCalendarDateRangeChange = useCallback(
    ({ startDate, endDate }) => {
      setCalendarDateRange({ startDate, endDate });
    },
    [],
  );

  // For calendar view, filter today's procedures from fetched data
  const scheduledForTodayCount = useMemo(() => {
    return fetchedProcedures.filter(
      (procedure) => procedure.start && isToday(procedure.start),
    ).length;
  }, [fetchedProcedures]);

  const pagination = useMemo(
    () => ({
      total,
      current: page,
      pageSize,
      pageSizeOptions: ['10', '15', '20'],
      size: 'small',
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      onChange: (newPage) => {
        setPage(newPage);
      },
      onShowSizeChange: (current, newPageSize) => {
        setPageSize(newPageSize);
        setPage(1); // Reset to first page when page size changes
      },
    }),
    [total, page, pageSize],
  );

  // Handle patient name filter - only apply on Enter or blur
  const handlePatientNameInputChange = (value) => {
    setPatientNameInput(value);
  };

  const commitPatientName = useCallback(() => {
    if (patientNameInput !== patientName) {
      setPatientName(patientNameInput);
      setPage(1);
    }
  }, [patientNameInput, patientName]);

  const handlePatientNameKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        commitPatientName();
      }
    },
    [commitPatientName],
  );

  const handleClinicianChange = (newClinicianId) => {
    if (newClinicianId === CLINICIAN_OPTION_ALL) {
      setClinicianId(null);
    } else {
      const numericId =
        typeof newClinicianId === 'string'
          ? parseInt(newClinicianId, 10)
          : newClinicianId;
      setClinicianId(numericId);
    }
    setPage(1); // Reset to first page when filter changes
  };

  const clinicianOptions = useMemo(() => {
    const options = [
      { value: CLINICIAN_OPTION_ALL, label: t('all') },
      ...clinicians,
    ];
    return options;
  }, [clinicians, t]);

  const handleSelectRecord = useCallback((record) => {
    setIsDialogVisible(true);
    setCurrentProcedure(record);
  }, []);

  return (
    <>
      <Layout
        title={t('procedures')}
        description={t('screens:episodesOfCare.procedures.description')}
        sidebar={{
          mode,
          onSetMode: setMode,
          statistics: (
            <>
              <Card data-testid="card-total" title={t('total')} bordered>
                <span
                  data-testid="card-total-value"
                  className="text-xl font-semibold"
                >
                  {total}
                </span>
              </Card>
              <Card
                data-testid="card-today"
                type="info"
                title={t('today')}
                bordered
              >
                <div className="cluster-2 items-baseline">
                  <span
                    data-testid="card-today-value"
                    className="text-xl font-semibold"
                  >
                    {scheduledForTodayCount}
                  </span>
                  <span className="text-sm text-secondary">
                    {t('scheduledFor', {
                      date: toIntlDateTimeFormat(language, new Date()),
                    })}
                  </span>
                </div>
              </Card>
            </>
          ),
          filters: (
            <>
              <FormFieldSearch
                data-testid="filter-status"
                label={t('forms:status.label')}
                options={getProcedureStatusOptions({ t })}
                input={{
                  value: currentStatus,
                  onChange: (value) => {
                    setCurrentStatus(value);
                    setPage(1); // Reset to first page when filter changes
                  },
                }}
                meta={{}}
              />
              <FormFieldText
                data-testid="filter-patient"
                label={t('forms:patient.label')}
                placeholder={t('forms:patient.placeholder')}
                input={{
                  value: patientNameInput,
                  onChange: handlePatientNameInputChange,
                  onBlur: commitPatientName,
                  onKeyDown: handlePatientNameKeyDown,
                }}
                meta={{}}
              />
              <FormFieldSearch
                data-testid="filter-clinician"
                label={t('forms:clinician.label')}
                options={clinicianOptions}
                input={{
                  value: clinicianId || CLINICIAN_OPTION_ALL,
                  onChange: handleClinicianChange,
                }}
                meta={{}}
                loading={cliniciansLoading}
              />
            </>
          ),
        }}
      >
        <>
          {mode === 'table' && (
            <Card className="stack-4" bordered>
              <Table
                title={t('proceduresWithCount', {
                  count: total,
                })}
                dataSource={fetchedProcedures}
                columns={getProcedureColumns({ language, t })}
                pagination={pagination}
                loading={loading}
                onRow={(procedure) => ({
                  onClick: () => handleSelectRecord(procedure),
                })}
              />
            </Card>
          )}
          {mode === 'calendar' && (
            <CalendarTimeView
              className="h-full"
              events={calendarProcedures}
              isLoading={calendarLoading}
              onDateRangeChange={handleCalendarDateRangeChange}
            />
          )}
        </>
      </Layout>
      {currentProcedure && (
        <ProcedureDetails
          key={currentProcedure._id || currentProcedure.eventId}
          procedure={currentProcedure}
          visible={isDialogVisible}
          onCancel={() => setIsDialogVisible(false)}
        />
      )}
    </>
  );
};

export default Procedures;
